import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, BookOpen, Camera, Loader, Plus, Smartphone, Monitor } from 'lucide-react';

const MAX_PAGES = 6;
const QR_API = (url) => `https://api.qrserver.com/v1/create-qr-code/?size=220x220&qzone=1&data=${encodeURIComponent(url)}`;

// ─── Tab: Da file ─────────────────────────────────────────────────────────────

const TabFile = ({ onImport, isLoading, error, onClose }) => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const addFiles = useCallback((newFiles) => {
    const valid = Array.from(newFiles).filter(f => f.type.startsWith('image/'));
    if (!valid.length) return;
    setFiles(prev => [...prev, ...valid].slice(0, MAX_PAGES));
    setPreviews(prev => [...prev, ...valid.map(f => URL.createObjectURL(f))].slice(0, MAX_PAGES));
  }, []);

  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => { URL.revokeObjectURL(prev[index]); return prev.filter((_, i) => i !== index); });
  }, []);

  const handleDrop = useCallback((e) => { e.preventDefault(); setIsDragging(false); addFiles(e.dataTransfer.files); }, [addFiles]);
  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback(() => setIsDragging(false), []);
  const handleInputChange = useCallback((e) => { addFiles(e.target.files); e.target.value = ''; }, [addFiles]);

  const handleSubmit = useCallback(async () => {
    if (!files.length || isLoading) return;
    const ok = await onImport(files);
    if (ok) onClose();
  }, [files, isLoading, onImport, onClose]);

  const hasFiles = files.length > 0;

  return (
    <>
      {!hasFiles ? (
        <div
          onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragging ? '#1e66f5' : 'var(--border-s)'}`,
            borderRadius: '14px',
            padding: '32px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            background: isDragging ? 'color-mix(in srgb, #1e66f5 6%, transparent)' : 'var(--surface)',
            transition: 'all 0.15s',
            marginBottom: '16px',
          }}
        >
          <Camera size={28} color={isDragging ? '#1e66f5' : 'var(--text-muted)'} style={{ margin: '0 auto 12px', display: 'block', transition: 'color 0.15s' }} />
          <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px' }}>Trascina le foto qui</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>puoi selezionarne più di una — max {MAX_PAGES}</div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 16px', borderRadius: '8px', background: '#1e66f5', color: '#fff', fontSize: '13px', fontWeight: 600 }}>
            <Upload size={13} /> Scegli immagini
          </div>
          <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--text-muted)', letterSpacing: '0.04em' }}>JPG, PNG, WEBP</div>
        </div>
      ) : (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {previews.map((url, i) => (
              <div key={i} style={{ position: 'relative', aspectRatio: '3/4' }}>
                <img src={url} alt={`Pagina ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg)' }} />
                <div style={{ position: 'absolute', top: '4px', left: '4px', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px' }}>{i + 1}</div>
                <button onClick={() => removeFile(i)} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%', cursor: 'pointer', padding: '3px', color: '#fff', display: 'flex', lineHeight: 1 }}><X size={12} /></button>
              </div>
            ))}
            {files.length < MAX_PAGES && (
              <div
                onClick={() => fileInputRef.current?.click()}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#1e66f5'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                style={{ aspectRatio: '3/4', border: '2px dashed var(--border)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: '4px', color: 'var(--text-muted)', fontSize: '11px', background: 'var(--bg)', transition: 'border-color 0.15s' }}
              >
                <Plus size={18} /> aggiungi
              </div>
            )}
          </div>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" multiple onChange={handleInputChange} style={{ display: 'none' }} />

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', background: 'color-mix(in srgb, var(--danger) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)', color: 'var(--danger)', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={onClose} disabled={isLoading} style={{ flex: 1, padding: '10px', borderRadius: '10px', cursor: 'pointer', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '14px', fontWeight: 500 }}>
          Annulla
        </button>
        <button onClick={handleSubmit} disabled={!hasFiles || isLoading} style={{ flex: 2, padding: '10px', borderRadius: '10px', cursor: hasFiles && !isLoading ? 'pointer' : 'not-allowed', background: '#1e66f5', opacity: hasFiles && !isLoading ? 1 : 0.4, border: 'none', color: '#fff', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'opacity 0.15s' }}>
          {isLoading ? (<><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} />Estraggo i concetti…</>) : (<><BookOpen size={15} />{files.length > 1 ? `Genera mappa (${files.length} pagine)` : 'Genera mappa'}</>)}
        </button>
      </div>
    </>
  );
};

// ─── Tab: Da mobile ───────────────────────────────────────────────────────────

const TabMobile = ({ captureStatus, captureUrl, onStart, onStop, onClose, error }) => {
  const isIdle = captureStatus === 'idle';
  const isWaiting = captureStatus === 'waiting';
  const isProcessing = captureStatus === 'processing';
  const isDone = captureStatus === 'done';

  if (isDone) {
    setTimeout(onClose, 1200);
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
        <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text)' }}>Mappa generata!</div>
      </div>
    );
  }

  return (
    <>
      {isIdle && (
        <>
          <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
            <div style={{ width: 52, height: 52, borderRadius: '14px', margin: '0 auto 14px', background: 'color-mix(in srgb, #1e66f5 15%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Smartphone size={24} color="#1e66f5" />
            </div>
            <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text)', marginBottom: '6px' }}>Fotocamera del telefono</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Genera un QR code, scansionalo con il telefono e scatta la foto della pagina direttamente.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: '10px', cursor: 'pointer', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '14px', fontWeight: 500 }}>Annulla</button>
            <button onClick={onStart} style={{ flex: 2, padding: '10px', borderRadius: '10px', cursor: 'pointer', background: '#1e66f5', border: 'none', color: '#fff', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Smartphone size={15} /> Genera QR code
            </button>
          </div>
        </>
      )}

      {(isWaiting || isProcessing) && captureUrl && (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <div style={{ padding: '10px', background: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
              <img src={QR_API(captureUrl)} alt="QR code" width="200" height="200" style={{ display: 'block', borderRadius: '4px' }} />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderRadius: '10px', background: 'var(--bg)', border: '1px solid var(--border)', marginBottom: '16px' }}>
            {isProcessing ? (
              <Loader size={16} color="#1e66f5" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
            ) : (
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#40a02b', flexShrink: 0, animation: 'pulse 1.5s ease-in-out infinite' }} />
            )}
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
                {isProcessing ? 'Elaborazione in corso…' : 'In attesa della foto…'}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                {isProcessing ? "L'AI sta analizzando l'immagine" : 'Scansiona il QR con il telefono, poi scatta la foto'}
              </div>
            </div>
          </div>

          {error && (
            <div style={{ padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', background: 'color-mix(in srgb, var(--danger) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)', color: 'var(--danger)', fontSize: '13px' }}>
              {error}
            </div>
          )}

          <button onClick={onStop} disabled={isProcessing} style={{ width: '100%', padding: '10px', borderRadius: '10px', cursor: isProcessing ? 'not-allowed' : 'pointer', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '14px', fontWeight: 500 }}>
            Annulla
          </button>
        </>
      )}

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </>
  );
};

// ─── Modal wrapper ────────────────────────────────────────────────────────────

export const ImportMapModal = ({ onImport, isLoading, error, onClose, captureStatus, captureUrl, onStartCapture, onStopCapture }) => {
  const [tab, setTab] = useState('file');

  const tabStyle = (active) => ({
    flex: 1, padding: '8px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
    background: active ? 'var(--bg-card)' : 'transparent',
    color: active ? 'var(--text)' : 'var(--text-muted)',
    boxShadow: active ? '0 1px 4px rgba(0,0,0,0.15)' : 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    transition: 'all 0.15s'
  });

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--bg-dim)', border: '1px solid var(--border)', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BookOpen size={18} color="#1e66f5" />
            <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>Carica pagine libro</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', borderRadius: '6px' }}>
            <X size={18} />
          </button>
        </div>

        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg)', borderRadius: '10px', padding: '4px', marginBottom: '20px' }}>
          <button style={tabStyle(tab === 'file')} onClick={() => setTab('file')}>
            <Monitor size={14} /> Da file
          </button>
          <button style={tabStyle(tab === 'mobile')} onClick={() => setTab('mobile')}>
            <Smartphone size={14} /> Da mobile
          </button>
        </div>

        {tab === 'file'
          ? <TabFile onImport={onImport} isLoading={isLoading} error={error} onClose={onClose} />
          : <TabMobile captureStatus={captureStatus} captureUrl={captureUrl} onStart={onStartCapture} onStop={onStopCapture} onClose={onClose} error={error} isLoading={isLoading} />
        }
      </div>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>,
    document.body
  );
};
